import React, {
    useState,
    useContext,
    useCallback,
} from 'react';
import {
    Loader, useModal, Button, FormattedDate
} from '@jutro/components';
import { withRouter } from 'react-router-dom';
import { TranslatorContext } from '@jutro/locale';
import _ from 'lodash';
import { useAuthentication } from 'gw-digital-auth-react';
import { ViewModelForm, useDataRefresh, ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { ClaimService } from 'gw-capability-claim';
import { renderContentFromMetadataInternal } from '@jutro/uiconfig';
import messages from './FNOLPolicySearchAndSelectComponent_AX.messages';
import metadata from './FNOLPolicySearchAndSelectComponent_AX.metadata.json5';
import styles from './FNOLPolicySearchAndSelectComponent_AX.module.scss';

function FNOLWizardSearchAndSelectPolicyAX(props) {
    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);

    const { authHeader } = useAuthentication();
    const { refreshData } = useDataRefresh();

    const [isLoading, setLoadingState] = useState(false);
    const {
        showAlert
    } = useModal();

    const {
        setPolicySelected,
        policySearchCriteriaVM,
        onUpdateModel,
        policiesList,
        updateSearchResults,
        formReset,
        setClaimCreated
    } = props;

    // /**
    //  * Function used to set the available values to state dropdownselect field.
    //  */
    const handleStateItems = useCallback((option) => {
        if (!_.isUndefined(option.id)) {
            _.set(policySearchCriteriaVM, 'state', option.id);
            refreshData();
        }
    }, [policySearchCriteriaVM, refreshData]);

    const handleOriginItems = useCallback((option) => {
        if (!_.isUndefined(option.id)) {
            _.set(policySearchCriteriaVM, 'origin_AX', option.id);
            refreshData();
        }
    }, [policySearchCriteriaVM, refreshData]);

    const resetForm = useCallback((e) => {
        e.preventDefault();
        onUpdateModel(viewModelService.create(
            {
                lossDate: new Date(),
                origin_AX: 'autos',
                country: 'MX'
            },
            'cc',
            'edge.capabilities.claim.fnol.policy.dto.PolicySearchCriteriaDTO'
        ));
        updateSearchResults([]);
        setPolicySelected(undefined);
        formReset();
        refreshData();
    }, [formReset, onUpdateModel, refreshData, setPolicySelected,
        updateSearchResults, viewModelService]);

    const handleResponse = useCallback((response) => {
        if (response.length === 0) {
            showAlert({
                status: 'info',
                icon: 'mi-info',
                title: translator(messages.noResults),
                message: translator(messages.noResultsMessage)
            });
        }
        setPolicySelected(undefined);
        if (response.length === 1) {
            const policy = _.set(response[0], 'ax_Selected', true);
            setPolicySelected({ ...policy });
        }
        updateSearchResults([...response]);
        refreshData();
    }, [refreshData, setPolicySelected, translator, updateSearchResults]);

    const isEmptyCriteria = (criteria) => {
        return criteria.policyNumber.value === undefined
            && criteria.companyName.value === undefined
            && criteria.firstName.value === undefined
            && criteria.lastName.value === undefined
            && criteria.secondLastName_AX.value === undefined
            && criteria.clauseNumber_AX.value === undefined
            && criteria.state.value === undefined
            && criteria.vin.value === undefined
            && criteria.licensePlate_AX.value === undefined;
    };

    const searchPolicies = useCallback(async (e) => {
        e.preventDefault();
        _.set(policySearchCriteriaVM, 'lossDate', new Date());
        if (isEmptyCriteria(policySearchCriteriaVM)) {
            showAlert({
                status: 'error',
                icon: 'mi-error',
                title: translator(messages.error),
                message: translator(messages.errorMessage)
            });
            return;
        }
        setLoadingState(true);
        await ClaimService.simplifiedSearchPolicy(
            policySearchCriteriaVM.value,
            authHeader
        ).then(handleResponse)
            // eslint-disable-next-line no-console
            .catch((error) => console.log(error))
            .finally(() => {
                setLoadingState(false);
                setClaimCreated(false);
            });
    }, [policySearchCriteriaVM, authHeader, handleResponse, translator]);

    const handleModelChange = useCallback((newModel) => {
        onUpdateModel(newModel);
        refreshData();
    }, [onUpdateModel, refreshData]);

    const handlerSelectVMChange = useCallback((e, rowPolicy, policiesList) => {
        e.preventDefault();
        if (!rowPolicy.ax_Selected) {
            policiesList.forEach((item) => {
                item.ax_Selected = false;
            });
        }
        rowPolicy.ax_Selected = !rowPolicy.ax_Selected
        if (rowPolicy.ax_Selected) {
            setPolicySelected({...rowPolicy})
        } else {
            setPolicySelected(undefined);
        }
        updateSearchResults([...policiesList])
        refreshData();
    }, [setPolicySelected, updateSearchResults, refreshData]);

    const renderSelectedValue = (policySelected) => {
        if (policySelected) {
            return translator(messages.deselect);
        }
        return translator(messages.select);
    };

    const renderSelectButton = useCallback((rowPolicy, rowIndex) => {
        return (
            <Button
                type="filled"
                size="small"
                onClick={(e) => handlerSelectVMChange(e, rowPolicy, policiesList)}
                >{renderSelectedValue(rowPolicy.ax_Selected)}</Button>
        );
    }, [policiesList]);

    const renderVehicleDescription = useCallback((rowPolicy) => {
        const _make = rowPolicy.vehicleInvolved.make == undefined? "" : rowPolicy.vehicleInvolved.make
        const _model = rowPolicy.vehicleInvolved.model == undefined? "" : rowPolicy.vehicleInvolved.model
        const _yearNumber = rowPolicy.vehicleInvolved.year == undefined? 0 : rowPolicy.vehicleInvolved.year
        const _year = _yearNumber > 0 ? _yearNumber + "" : ""
        return (
            <span>{_make + " " + _model + " " + _year}</span>
        );
    }, []);

    const renderEffectiveDateInput = useCallback((rowPolicy) => {
        const _effDate = rowPolicy.effectiveDate
        if (typeof _effDate === 'undefined') {
            return '';
        }    
        return <FormattedDate value={_effDate} format="long" />;
    }, []);

    const renderExpirationDateInput = useCallback((rowPolicy) => {
        const _expDate = rowPolicy.expirationDate
        if (typeof _expDate === 'undefined') {
            return '';
        }    
        return <FormattedDate value={_expDate} format="long" />;
    }, []);

    const renderPolicyStatusName = useCallback((rowPolicy) => {
        const _name = translator({
            id: `typekey.PolicyStatus.${rowPolicy.policyStatus}`,
            defaultMessage: rowPolicy.policyStatus
        })
        return (
            <span>{_name}</span>
        );
    }, []);

    const handlerNextStep = useCallback(() => {
    }, []);

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    const overrides = {
        searchPolicyForm: {
            className: styles.container_bottom
        },
        infoNotification: {
            className: styles.container_bottom,
            visible: policiesList.length > 0
        },
        policiesTable: {
            data: policiesList
        },
        lossDateForm: {
            visible: policiesList.length > 0
        },
        selectButton: {
            renderCell: renderSelectButton
        },
        addressLine1Input: {
            visible: policiesList.some(pol => pol.isProperty === true),
            sortable: false
        },
        vehicleDescription: {
            visible: !policiesList.some(pol => pol.isProperty === true),
            renderCell: renderVehicleDescription,
            sortable: false
        },
        vinInput: {
            visible: !policiesList.some(pol => pol.isProperty === true),
            sortable: false
        },
        insuredInput: {
            sortable: false
        },
        policyCollectionStatusInput: {
            sortable: false
        },
        licensePlateInput: {
            visible: !policiesList.some(pol => pol.isProperty === true),
            sortable: false
        },
        policyStatusInput: {
            renderCell: renderPolicyStatusName,
            sortable: false
        },
        effectiveDateInput: {
            renderCell: renderEffectiveDateInput
        },
        expirationDateInput: {
            renderCell: renderExpirationDateInput
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            searchPolicies: searchPolicies,
            resetForm: resetForm,
            handleStateItems: handleStateItems,
            handleOriginItems: handleOriginItems,
            handlerNextStep: handlerNextStep
        }
    };

    return (
        <>
            <ViewModelForm
                uiProps={metadata.formComponentContent}
                model={policySearchCriteriaVM}
                onModelChange={handleModelChange}
                overrideProps={overrides}
                callbackMap={resolvers.resolveCallbackMap}
            />
            {
                policiesList.length > 0
                && renderContentFromMetadataInternal(
                    metadata.policiesTableComponentContent.content,
                    overrides,
                    resolvers
                )
            }
            <ViewModelForm
                uiProps={metadata.lossDateComponentContent}
                model={policySearchCriteriaVM}
                onModelChange={handleModelChange}
                overrideProps={overrides}
                callbackMap={resolvers.resolveCallbackMap}
            />
        </>
    );
}

export default withRouter(FNOLWizardSearchAndSelectPolicyAX);
